import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { 
  Bot, CheckCircle, XCircle, Edit, ChevronLeft, ChevronRight,
  Save, AlertCircle, Loader2, FileText, Award, User
} from 'lucide-react';
import { toast } from 'sonner';

const AIGradingReview = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const user = getUser();
  
  const [loading, setLoading] = useState(true);
  const [gradingResult, setGradingResult] = useState(null);
  const [exam, setExam] = useState(null);
  const [currentPartIndex, setCurrentPartIndex] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editMarks, setEditMarks] = useState(0);
  const [editFeedback, setEditFeedback] = useState('');
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    fetchGradingResult();
  }, [submissionId]);

  const fetchGradingResult = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/ai-grading/review/${submissionId}`);
      setGradingResult(res.data);
      setExam(res.data.exam);
      
      // Initialize edit values with current part
      if (res.data.graded_parts?.length > 0) {
        const part = res.data.graded_parts[0];
        setEditMarks(part.teacher_marks ?? part.ai_marks);
        setEditFeedback(part.teacher_feedback || part.ai_feedback);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load grading results');
      if (err.response?.status === 404) {
        navigate(-1);
      }
    } finally {
      setLoading(false);
    }
  };

  const currentPart = gradingResult?.graded_parts?.[currentPartIndex];
  const totalParts = gradingResult?.graded_parts?.length || 0;

  const navigatePart = (direction) => {
    const newIndex = currentPartIndex + direction;
    if (newIndex >= 0 && newIndex < totalParts) {
      setCurrentPartIndex(newIndex);
      setEditMode(false);
      const part = gradingResult.graded_parts[newIndex];
      setEditMarks(part.teacher_marks ?? part.ai_marks);
      setEditFeedback(part.teacher_feedback || part.ai_feedback);
    }
  };

  const startEdit = () => {
    setEditMode(true);
    setEditMarks(currentPart.teacher_marks ?? currentPart.ai_marks);
    setEditFeedback(currentPart.teacher_feedback || currentPart.ai_feedback);
  };

  const cancelEdit = () => {
    setEditMode(false);
  };

  const savePartApproval = async (useAiMarks = false) => {
    setSaving(true);
    try {
      const marks = useAiMarks ? currentPart.ai_marks : editMarks;
      const feedback = useAiMarks ? currentPart.ai_feedback : editFeedback;
      
      await api.put(
        `/ai-grading/review/${submissionId}/part?question_idx=${currentPart.question_idx}&part_idx=${currentPart.part_idx}&sub_part_idx=${currentPart.sub_part_idx}`,
        {
          teacher_marks: marks,
          teacher_feedback: feedback,
          is_approved: true
        }
      );
      
      // Update local state
      const updatedParts = [...gradingResult.graded_parts];
      updatedParts[currentPartIndex] = {
        ...currentPart,
        teacher_marks: marks,
        teacher_feedback: feedback,
        is_approved: true
      };
      setGradingResult({ ...gradingResult, graded_parts: updatedParts });
      setEditMode(false);
      
      toast.success('Part approved');
      
      // Auto-navigate to next unapproved part
      const nextUnapproved = updatedParts.findIndex((p, i) => i > currentPartIndex && !p.is_approved);
      if (nextUnapproved !== -1) {
        setCurrentPartIndex(nextUnapproved);
        const part = updatedParts[nextUnapproved];
        setEditMarks(part.teacher_marks ?? part.ai_marks);
        setEditFeedback(part.teacher_feedback || part.ai_feedback);
      }
    } catch (err) {
      toast.error('Failed to save approval');
    } finally {
      setSaving(false);
    }
  };

  const approveAllWithAI = async () => {
    if (!window.confirm('This will approve all parts with AI-suggested marks. Continue?')) return;
    
    setSaving(true);
    try {
      await api.post(`/ai-grading/review/${submissionId}/approve-all`);
      await fetchGradingResult();
      toast.success('All parts approved with AI marks');
    } catch (err) {
      toast.error('Failed to approve all');
    } finally {
      setSaving(false);
    }
  };

  const finalizeGrading = async () => {
    const unapproved = gradingResult.graded_parts.filter(p => !p.is_approved);
    if (unapproved.length > 0) {
      toast.error(`${unapproved.length} parts have not been approved yet`);
      return;
    }
    
    if (!window.confirm('Finalize grading? This will save the grades to the submission.')) return;
    
    setFinalizing(true);
    try {
      const res = await api.post(`/ai-grading/review/${submissionId}/finalize`);
      toast.success(`Grading finalized! Final grade: ${res.data.final_grade}`);
      navigate(-1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to finalize');
    } finally {
      setFinalizing(false);
    }
  };

  const getQuestionText = (qIdx) => {
    if (!exam?.questions) return '';
    const q = exam.questions[qIdx];
    return q?.question_text || '';
  };

  const approvedCount = gradingResult?.graded_parts?.filter(p => p.is_approved).length || 0;
  const progressPercent = totalParts > 0 ? Math.round((approvedCount / totalParts) * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto" />
          <p className="mt-4 text-slate-600">Loading AI grading results...</p>
        </div>
      </div>
    );
  }

  if (!gradingResult || gradingResult.status === 'pending' || gradingResult.status === 'grading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-xl shadow-sm border border-slate-200">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto" />
          <h2 className="mt-4 text-xl font-semibold text-slate-900">AI Grading in Progress</h2>
          <p className="mt-2 text-slate-600">Please wait while the AI grades this submission...</p>
          <button
            onClick={fetchGradingResult}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Refresh Status
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <Bot className="w-8 h-8 text-indigo-600" />
              <div>
                <h1 className="text-xl font-bold text-slate-900">AI Grading Review</h1>
                <p className="text-sm text-slate-500">{gradingResult.student_name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Progress */}
              <div className="text-right">
                <p className="text-sm font-medium text-slate-700">
                  {approvedCount} / {totalParts} Approved
                </p>
                <div className="w-32 h-2 bg-slate-200 rounded-full mt-1">
                  <div 
                    className="h-full bg-green-500 rounded-full transition-all"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
              
              {/* AI Summary */}
              <div className="px-4 py-2 bg-indigo-50 rounded-lg">
                <p className="text-sm text-indigo-600">
                  AI Score: <span className="font-bold">{gradingResult.ai_total_marks}/{gradingResult.ai_max_marks}</span>
                  <span className="ml-2">({gradingResult.ai_percentage}%)</span>
                  <span className="ml-2 font-bold">{gradingResult.ai_grade}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4">
        {/* Part Navigator */}
        <div className="bg-white rounded-xl border border-slate-200 mb-4 p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigatePart(-1)}
              disabled={currentPartIndex === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            
            <div className="flex items-center gap-2">
              {gradingResult.graded_parts.map((part, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setCurrentPartIndex(idx);
                    setEditMode(false);
                    setEditMarks(part.teacher_marks ?? part.ai_marks);
                    setEditFeedback(part.teacher_feedback || part.ai_feedback);
                  }}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                    idx === currentPartIndex
                      ? 'bg-indigo-600 text-white'
                      : part.is_approved
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  title={`Q${part.question_idx + 1} ${part.part_label}`}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => navigatePart(1)}
              disabled={currentPartIndex === totalParts - 1}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Current Part Review */}
        {currentPart && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left: Question & Student Answer */}
            <div className="space-y-4">
              {/* Question */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <h2 className="font-semibold text-slate-900">
                    Question {currentPart.question_idx + 1} - Part {currentPart.part_label}
                  </h2>
                  <span className="ml-auto px-2 py-1 bg-indigo-100 text-indigo-700 text-sm rounded">
                    {currentPart.max_marks} marks
                  </span>
                </div>
                <p className="text-slate-700 whitespace-pre-wrap">
                  {getQuestionText(currentPart.question_idx)}
                </p>
              </div>
              
              {/* Student Answer */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-5 h-5 text-slate-400" />
                  <h2 className="font-semibold text-slate-900">Student's Answer</h2>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  {currentPart.student_answer ? (
                    <p className="text-slate-700 whitespace-pre-wrap">{currentPart.student_answer}</p>
                  ) : (
                    <p className="text-slate-400 italic">No answer provided</p>
                  )}
                </div>
              </div>
              
              {/* Marking Scheme */}
              {currentPart.marking_scheme && (
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Award className="w-5 h-5 text-amber-500" />
                    <h2 className="font-semibold text-slate-900">Marking Scheme</h2>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-slate-700 whitespace-pre-wrap text-sm">{currentPart.marking_scheme}</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Right: AI Grade & Teacher Review */}
            <div className="space-y-4">
              {/* AI Grading Result */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Bot className="w-5 h-5 text-indigo-600" />
                  <h2 className="font-semibold text-indigo-900">AI Assessment</h2>
                  {currentPart.is_approved && (
                    <CheckCircle className="w-5 h-5 text-green-500 ml-auto" />
                  )}
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-indigo-700">AI Marks:</span>
                    <span className="text-2xl font-bold text-indigo-900">
                      {currentPart.ai_marks} / {currentPart.max_marks}
                    </span>
                  </div>
                  
                  <div>
                    <p className="text-sm text-indigo-700 mb-1">AI Feedback:</p>
                    <p className="text-slate-700 bg-white/50 rounded-lg p-3">
                      {currentPart.ai_feedback}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Teacher Review Section */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4">Teacher Review</h2>
                
                {currentPart.is_approved && !editMode ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Approved Marks:</span>
                      <span className="text-2xl font-bold text-green-600">
                        {currentPart.teacher_marks ?? currentPart.ai_marks} / {currentPart.max_marks}
                      </span>
                    </div>
                    
                    {(currentPart.teacher_feedback || currentPart.ai_feedback) && (
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Feedback:</p>
                        <p className="text-slate-700 bg-slate-50 rounded-lg p-3">
                          {currentPart.teacher_feedback || currentPart.ai_feedback}
                        </p>
                      </div>
                    )}
                    
                    <button
                      onClick={startEdit}
                      className="flex items-center gap-2 px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                    >
                      <Edit className="w-4 h-4" />
                      Edit Approval
                    </button>
                  </div>
                ) : editMode ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Marks (max: {currentPart.max_marks})
                      </label>
                      <input
                        type="number"
                        value={editMarks}
                        onChange={(e) => setEditMarks(Math.max(0, Math.min(currentPart.max_marks, parseFloat(e.target.value) || 0)))}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        min="0"
                        max={currentPart.max_marks}
                        step="0.5"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Feedback (optional)
                      </label>
                      <textarea
                        value={editFeedback}
                        onChange={(e) => setEditFeedback(e.target.value)}
                        rows={3}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="Add feedback..."
                      />
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => savePartApproval(false)}
                        disabled={saving}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save & Approve
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <button
                      onClick={() => savePartApproval(true)}
                      disabled={saving}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                      Accept AI Grade ({currentPart.ai_marks}/{currentPart.max_marks})
                    </button>
                    
                    <button
                      onClick={startEdit}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50"
                    >
                      <Edit className="w-5 h-5" />
                      Modify Marks
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="mt-6 bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={approveAllWithAI}
              disabled={saving || approvedCount === totalParts}
              className="flex items-center gap-2 px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg disabled:opacity-50"
            >
              <Bot className="w-4 h-4" />
              Approve All with AI Marks
            </button>
            
            <div className="flex items-center gap-4">
              {approvedCount < totalParts && (
                <p className="text-sm text-amber-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {totalParts - approvedCount} parts remaining
                </p>
              )}
              
              <button
                onClick={finalizeGrading}
                disabled={finalizing || approvedCount < totalParts}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {finalizing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-5 h-5" />
                )}
                Finalize Grading
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AIGradingReview;
